import { currentOrgId } from "../api/client";
import { getLocale, setLocale, subscribeLocale, type Locale } from "../i18n";
import { orgs } from "../api/orgs";
import { idb } from "./idb";
import { isOnline, onNetwork } from "./network";
import { pruneOrgs } from "./offlineCache";
import { setMemberOrgs } from "./outbox";

/*
 * Organizations on this device (wave 6):
 *
 *   The device's organization. Each tab works in its own organization (client.ts, sessionStorage). The one this device
 *   used last is the organization of the tab that was last shown (it is written when a tab signs in, switches, or comes to
 *   the front), kept in IndexedDB "prefs" (an organization id, nothing personal). A window with no organization of its
 *   own, such as a home-screen shortcut, asks for that one at session restore (AuthContext), not the one the person used
 *   last on any device; the server still checks membership and falls back to its own choice.
 *
 *   The online check. While signed in, the person's organizations are asked for (GET /admin/organizations) when the
 *   server answers again after a break, when the app comes to the front, and every 10 minutes. Copies of an organization
 *   the person has left are deleted at once (pruneOrgs), and outbox items made there stop being sent and wait to be
 *   exported or discarded (setMemberOrgs).
 *
 *   The language. The screens' language and formats arrive from the server after sign-in; the device keeps the last ones
 *   ("locale" in prefs) so the offline screen after a reload with no connection is in the person's language too.
 */

const DEVICE_KEY = "device-org";
type Pref<T> = { key: string; value: T; at: number };

export async function rememberDeviceOrg(org: number | null = currentOrgId()) {
  if (!org) return;
  await idb.put<Pref<number>>("prefs", { key: DEVICE_KEY, value: org, at: Date.now() }).catch(() => undefined);
}

/** The organization of the tab this device showed last, or null. */
export async function deviceOrg(): Promise<number | null> {
  const p = await idb.get<Pref<number>>("prefs", DEVICE_KEY).catch(() => undefined);
  return typeof p?.value === "number" && p.value > 0 ? p.value : null;
}

/** A small setting of this device (the install prompt's "Not now"), kept across sign-outs. */
export async function devicePref<T>(key: string): Promise<{ value: T; at: number } | null> {
  const p = await idb.get<Pref<T>>("prefs", key).catch(() => undefined);
  return p ? { value: p.value, at: p.at } : null;
}
export const setDevicePref = <T,>(key: string, value: T) => idb.put<Pref<T>>("prefs", { key, value, at: Date.now() }).catch(() => undefined);

/** The organizations the person belongs to now, as the server says: prunes what belongs to any other. */
let lastCheck = 0;
export async function checkMembership(force = false): Promise<number[] | null> {
  if (!isOnline() || (!force && Date.now() - lastCheck < 30_000)) return null;
  lastCheck = Date.now();
  try {
    const list = await orgs.list();
    const member = list.organizations.map((o) => o.id);
    setMemberOrgs(member);
    await pruneOrgs(member);
    return member;
  } catch {
    return null;
  }
}

const LOCALE_KEY = "locale";
/** The offline screen: the language and formats the person last had on this device. */
export async function restoreDeviceLocale() {
  const p = await devicePref<Locale>(LOCALE_KEY);
  if (p?.value) setLocale(p.value);
}

let stop: (() => void) | null = null;

/** From sign-in until sign-out: keeps the device's organization and the membership check going. */
export function startOrgCheck(member: number[]) {
  stopOrgCheck();
  setMemberOrgs(member);
  void rememberDeviceOrg();
  let wasOnline = isOnline();
  const offNet = onNetwork(() => {
    const now = isOnline();
    if (now && !wasOnline) void checkMembership(true);
    wasOnline = now;
  });
  const shown = () => {
    if (document.visibilityState !== "visible") return;
    void rememberDeviceOrg();
    void checkMembership();
  };
  document.addEventListener("visibilitychange", shown);
  window.addEventListener("focus", shown);
  const timer = window.setInterval(() => void checkMembership(), 600_000);
  const offLocale = subscribeLocale(() => {
    const { language, number_format, date_format } = getLocale();
    void setDevicePref<Locale>(LOCALE_KEY, { language, number_format, date_format });
  });
  stop = () => {
    offLocale();
    offNet();
    document.removeEventListener("visibilitychange", shown);
    window.removeEventListener("focus", shown);
    window.clearInterval(timer);
  };
}

export function stopOrgCheck() {
  stop?.();
  stop = null;
}
